import { MortgageCalcData } from '@propertypal/shared/src/hooks/useMortgageCalculator';
import { getItem } from '@propertypal/shared/src/services/local.storage';
import { Property, KeyInfo } from '@propertypal/shared/src/types/property';
import floatToCurrency, { parseNumber } from '@propertypal/shared/src/utils/floatToCurrency';
import {
  getDefaultDeposit,
  getDefaultTerm,
} from '@propertypal/shared/src/utils/property/getMortgageCalculatorDefaults';
import groupKeyInfo, { KeyInfoCell } from '@propertypal/shared/src/utils/property/groupKeyInfo';
import Link from 'next/link';
import React, { FunctionComponent, useEffect, useState } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { DefaultTheme, useTheme } from 'styled-components';
import { Text } from '../typography';
import EnergyRatingIcon from './EnergyRatingIcon';
import { Container, LinkText, Row, TableColumn, TBody } from './KeyInfoBox.style';

interface Props {
  buyingStatus: string;
  property: Property;
  onClick: (info: KeyInfo) => void;
  mortgageCalculator: MortgageCalcData;
}

const HIGHLIGHTED_ROWS = ['Sale agreed', 'Let agreed', 'Sold', 'Let'];

const STAMP_DUTY_TEXT_MAP: { [key: string]: string } = {
  FIRST_TIME_BUYER: 'First Time Buyer',
  HOME_MOVER: 'Home Mover',
  BUY_TO_LET_INVESTOR: 'Buy-to-Let Investor',
  ADDITIONAL_HOME_BUYER: 'Second Home Buyer',
};

const renderFeesLink = (accNumber: string) => {
  return (
    <Link href={`/letting-agent-fees/${accNumber}`} passHref>
      <LinkText as="a" data-testid="fees-link">
        +fees
      </LinkText>
    </Link>
  );
};

const renderText = (
  info: KeyInfoCell,
  property: Property,
  color: string,
  onClick: Props['onClick'],
  buyingStatus: string,
  mortgageCalcLoading: boolean,
  theme: DefaultTheme,
) => {
  const handleClick = () => onClick(info);

  if (info.buyerTypeCosts && buyingStatus) {
    return (
      <Text data-testid="stampDutyText">
        {info.buyerTypeCosts[buyingStatus]}** (Based on being a {STAMP_DUTY_TEXT_MAP[buyingStatus]}){' '}
        <LinkText
          as="button"
          aria-label="Change buying status"
          className="pp-stamp-duty-text"
          onClick={handleClick}
          type="button"
        >
          Change
        </LinkText>
      </Text>
    );
  }

  if (info.key === 'STAMP_DUTY_COMPLEX') {
    return (
      <LinkText
        as="button"
        className="pp-stamp-duty-text"
        data-testid="stampDutyText"
        onClick={handleClick}
        type="button"
      >
        Calculate Stamp Duty
      </LinkText>
    );
  }

  if (info.key === 'TYPICAL_MORTGAGE') {
    if (mortgageCalcLoading)
      return (
        <span data-testid="typical-mortgage-loading">
          <Loader color={theme.backgroundLight} data-testid="typical-mortgage-loading" />
        </span>
      );
    if (!info.text)
      return (
        <Text data-testid="no-mortgage-result">
          No results, try changing your{' '}
          <LinkText onClick={handleClick} as="span">
            mortgage criteria
          </LinkText>{' '}
          below
        </Text>
      );
    return (
      <>
        <LinkText as="p" onClick={handleClick} data-testid="typical-mortgage-text">
          {info.text}
        </LinkText>
        {info.subtext && (
          <Text data-testid="typical-mortgage-subtext" fontSize={13} color={theme.textLight}>
            {info.subtext}
          </Text>
        )}
      </>
    );
  }

  if (info.key === 'EPC' && info.epcRows) {
    return info.epcRows.map((epcInfo) => (
      <LinkText
        as="button"
        aria-label="View EPC file"
        className="pp-epc-text"
        data-testid="epcText"
        onClick={() => onClick(epcInfo)}
        key={epcInfo.text}
        type="button"
      >
        {epcInfo.key === 'EPC_FILES' && 'EPC - '}
        {epcInfo.text}
      </LinkText>
    ));
  }

  if (info.key === 'PRICE') {
    return (
      <Text className="pp-key-info-price">
        {!!info.beforePrice && `${info.beforePrice} `}
        <b style={{ color }}>{info.price}</b>
        {` ${info.afterPrice} `}
        {property.includeFeeCharges && renderFeesLink(property.account.accountNumber)}
      </Text>
    );
  }

  if (info.key === 'RATES' || info.key === 'STAMP_DUTY_SIMPLE') {
    return <Text>{info.text}*</Text>;
  }

  if (info.key === 'BER') {
    return <EnergyRatingIcon property={property} />;
  }

  return <Text>{info.text}</Text>;
};

const KeyInfoBox: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const {
    data,
    loading,
    mortgagePayment,
    hasResult,
    error,
    depositPercentage,
    getMortgageResult,
    hasMortgageCalculator,
  } = props.mortgageCalculator;

  const getKeyInfo = () => {
    if (props.property.keyInfo && hasMortgageCalculator) {
      const typicalMortgage: KeyInfo = {
        key: 'TYPICAL_MORTGAGE',
        name: 'Typical Mortgage',
        text: '',
        textHtml: '',
        subtext: null,
        image: null,
        link: null,
      };

      if (!loading && data && mortgagePayment && hasResult) {
        const text = !loading && data && mortgagePayment ? `${mortgagePayment} per month` : '';
        typicalMortgage.text = text;
        typicalMortgage.textHtml = text;
        typicalMortgage.subtext = `${floatToCurrency(data.deposit, {
          precision: 0,
        })} (${depositPercentage}%) deposit (${data.term} year term)`;
      }

      const keyInfo = [...props.property.keyInfo];
      keyInfo.splice(3, 0, typicalMortgage);
      return keyInfo;
    }
    return props.property.keyInfo;
  };

  const [keyInfo, setKeyInfo] = useState<KeyInfo[] | undefined>(getKeyInfo());

  // update key info section with mortgage calc if hasMortgageCalculator
  useEffect(() => {
    if (hasMortgageCalculator) setKeyInfo(getKeyInfo());
  }, [props.mortgageCalculator, hasMortgageCalculator]);

  // retrieve mortgage calculator information from default values
  useEffect(() => {
    if (!data && !loading && !error && hasMortgageCalculator) {
      const defaultTerm = getItem<string>('defaultTerm');
      const defaultDeposit = getItem<string>('defaultDeposit');

      getMortgageResult({
        propertyValue: props.property.price?.price,
        paymentTerm: defaultTerm ? parseNumber(defaultTerm) : getDefaultTerm(props.property),
        deposit: defaultDeposit ? parseNumber(defaultDeposit) : getDefaultDeposit(props.property),
      });
    }
  }, [data, loading, error, hasMortgageCalculator]);

  if (!props.property.keyInfo) {
    return null;
  }

  return (
    <Container className="pp-key-info">
      <TBody>
        {groupKeyInfo(keyInfo).map((info, index, keyInfoCells) => {
          const isHighlighted = HIGHLIGHTED_ROWS.includes(info.text);
          const isOrphan = index + 1 === keyInfoCells.length && keyInfoCells.length % 2 !== 0;

          return (
            <Row
              key={info.key}
              className={isHighlighted ? 'pp-key-info-row highlighted' : 'pp-key-info-row'}
              isOrphan={isOrphan}
              totalRows={groupKeyInfo(keyInfo).length}
            >
              <TableColumn isHighlighted={isHighlighted}>
                <Text data-testid={`key-info-row-${index + 1}`} fontWeight="600">
                  {info.name}
                </Text>
              </TableColumn>

              <TableColumn isHighlighted={isHighlighted}>
                {renderText(info, props.property, theme.primary, props.onClick, props.buyingStatus, loading, theme)}
              </TableColumn>
            </Row>
          );
        })}
      </TBody>
    </Container>
  );
};

export default KeyInfoBox;
